import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Popconfirm, Form, message, Row, Col } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../index.css";
import AntDesignTable from "../common/antTable/AntDesignTable";
import { API_URL } from "../../config";
import CommonModal from "../common/commonModal/CommonModal";
import DoctorForm from "./DoctorForm";
import auth from "../../firebaseConfig";
import moment from "moment";
import { useTranslation } from "react-i18next";

const DoctorsContainer = () => {
  const [doctors, setDoctors] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const initialFormData = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { t } = useTranslation();
  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API_URL}/doctor/`);
      setDoctors(response.data);
    } catch (error) {
      console.error("There was an error fetching the doctor data!", error);
    }
  };

  const fetchDoctorDetails = async (doctor_id) => {
    try {
      const response = await axios.get(`${API_URL}/doctor/${doctor_id}`);
      setCurrentDoctor(response.data);
      form.setFieldsValue(response.data);
    } catch (error) {
      console.error("There was an error fetching the doctor details!", error);
    }
  };

  const handleEdit = async (record) => {
    await fetchDoctorDetails(record.doctor_id);
    setIsModalVisible(true);
  };

  const handleDelete = async (doctor_id) => {
    try {
      setSelectedRowKeys([]);
      await axios.delete(`${API_URL}/doctor/${doctor_id}`);
      message.success(t('message.doctorDeletedSuccessfully'));
      fetchDoctors();
    } catch (error) {
      console.error("There was an error deleting the doctor data!", error);
      message.error(t('message.failedDoctorDelete'));
    }
  };

  const handleOk = async () => {
    try {
      await form.validateFields();

      const formData = form.getFieldsValue();

      if (currentDoctor) {
        formData.doctor_id = currentDoctor.doctor_id;
        formData.firebase_id = currentDoctor.firebase_id;

        const response = await axios.put(
          `${API_URL}/doctor/${currentDoctor.doctor_id}`,
          formData,
        );

        message.success(t('message.doctorUpdatedSuccessfully'));
        setDoctors((prevDoctors) =>
          prevDoctors.map((doctor) =>
            doctor.doctor_id === currentDoctor.doctor_id
              ? { ...doctor, ...formData }
              : doctor,
          ),
        );
      } else {
        const firebaseResult = await createUserWithEmailAndPassword(
          auth,
          formData.email_address,
          formData.password,
        );

        formData.firebase_id = firebaseResult.user.uid;

        const response = await axios.post(`${API_URL}/doctor/`, formData);

        message.success(t('message.doctorCreatedSuccessfully'));
        setDoctors((prevDoctors) => [response.data, ...prevDoctors]);
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("There was an error saving the doctor data!", error);

      const errorMessage =
        error.response?.data?.detail || t('message.failedDoctorSave');
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleCreateNewDoctor = () => {
    form.resetFields();
    setCurrentDoctor(null);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: t('tableHeader.name'),
      dataIndex: "name",
      key: "name",
      render: (text, record) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: t('tableHeader.gender'),
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: t('tableHeader.age'),
      dataIndex: "age",
      key: "age",
      render: (_, record) => moment().diff(record.date_of_birth, "years"),
    },
    {
      title: t('tableHeader.phoneNumber'),
      dataIndex: "phone_number",
      key: "phone_number",
    },
    {
      title: t('tableHeader.email'),
      dataIndex: "email_address",
      key: "email_address",
    },

    {
      title: t('tableHeader.action'),
      key: "action",
      render: (text, record) => (
        <span>
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Button
                className="edit-button"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Popconfirm
                title={t('message.doctorDeleteConfirmation')}
                onConfirm={() => handleDelete(record.doctor_id)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
              >
                <Button
                  className="delete-button"
                  icon={<DeleteOutlined />}
                  style={{ marginLeft: 8 }}
                />
              </Popconfirm>
            </Col>
          </Row>
        </span>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleCreateNewDoctor}
      >
        {t('doctor.createNewDoctor')}
      </Button>
      <AntDesignTable
        columns={columns}
        tableData={doctors}
        setSelectedRowKeys={setSelectedRowKeys}
        selectedRowKeys={selectedRowKeys}
        rowKey="doctor_id"
      />
      <CommonModal
        title={currentDoctor ?  t('doctor.editDoctor') :  t('doctor.createNewDoctor')}
        visible={isModalVisible}
        onCancel={handleCancel}
        onConfirm={handleOk}
        confirmButtonText={currentDoctor ? t('common.update') : t('common.submit')}
        width={1000}
      >
        <DoctorForm form={form} initialFormData={currentDoctor ? 1 : 0} />
      </CommonModal>
    </React.Fragment>
  );
};

export default DoctorsContainer;
